// Settings / System Status Page.
//
// HONEST DE-MOCK (WS-6): the previous version of this page rendered a full
// editable settings form (server name, grace period, SMTP, ML scoring, danger
// zone) wired to backend routes that DO NOT EXIST — GET/PUT /settings,
// POST /admin/cache/flush, POST /analytics/churn/recompute. Every "save" landed
// in a catch block that silently pretended to succeed. That is exactly the kind
// of fake UI the upgrade is meant to remove.
//
// The only configuration surface the backend genuinely exposes is GET /health,
// which reflects deploy-time wiring (database + redis). This page now shows that
// real, live status and is honest that mutable settings persistence is not
// configured on this server. When a settings store is added, this page can grow
// an editable form against it.
import React, { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { Settings as SettingsIcon, RefreshCw, CheckCircle, XCircle } from 'lucide-react';

interface HealthCheck {
  status: string;
  latencyMs?: number;
  error?: string;
}

interface HealthResponse {
  status: string;
  version: string;
  uptime: number;
  timestamp: string;
  responseTime: number;
  checks: Record<string, HealthCheck>;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function SettingsPage() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest<HealthResponse>('/health');
      setHealth(data);
    } catch (err) {
      // GET /health returns 503 (and a body) when degraded; apiRequest throws on
      // non-2xx. Surface honestly rather than faking a healthy state.
      setHealth(null);
      setError(err instanceof Error ? err.message : 'Unable to reach the license server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const labelStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--text-muted)' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">System status and configuration</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} /> Refresh
        </button>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <AlertBanner kind="info">
          <strong>Configuration is managed at deploy time.</strong> This license server does not expose an editable settings store. Operational parameters (grace periods, email/SMTP, rate limits) are configured via environment variables and Ansible at deploy time. The live subsystem status below is read from the server's health endpoint.
        </AlertBanner>
      </div>

      {error && (
        <div style={{ marginBottom: '16px' }}>
          <AlertBanner kind="error"><strong>Server unreachable or degraded.</strong> {error}</AlertBanner>
        </div>
      )}

      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">
          <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SettingsIcon size={15} /> Server
          </span>
        </div>
        <div className="card-body">
          {loading ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>Loading status…</div>
          ) : health ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
              <div>
                <div style={labelStyle}>Overall</div>
                <div style={{ fontSize: '15px', fontWeight: 600, textTransform: 'capitalize', color: health.status === 'healthy' ? '#16A34A' : '#D97706' }}>{health.status}</div>
              </div>
              <div>
                <div style={labelStyle}>Version</div>
                <div style={{ fontSize: '15px', fontWeight: 600 }}>{health.version}</div>
              </div>
              <div>
                <div style={labelStyle}>Uptime</div>
                <div style={{ fontSize: '15px', fontWeight: 600 }}>{formatUptime(health.uptime)}</div>
              </div>
              <div>
                <div style={labelStyle}>Health Response</div>
                <div style={{ fontSize: '15px', fontWeight: 600 }}>{health.responseTime}ms</div>
              </div>
            </div>
          ) : (
            <div style={{ padding: '12px 0', fontSize: '13px', color: 'var(--text-muted)' }}>
              Server status is not available.
            </div>
          )}
        </div>
      </div>

      {health && Object.keys(health.checks).length > 0 && (
        <div className="card">
          <div className="card-header"><span className="card-title">Subsystems</span></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(health.checks).map(([name, c]) => {
              const healthy = c.status === 'healthy';
              return (
                <div key={name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {healthy ? <CheckCircle size={16} color="#16A34A" /> : <XCircle size={16} color="#DC2626" />}
                    <span style={{ fontSize: '13px', fontWeight: 600, textTransform: 'capitalize' }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {typeof c.latencyMs === 'number' && (
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{c.latencyMs}ms</span>
                    )}
                    <span style={{ fontSize: '12px', fontWeight: 600, color: healthy ? '#16A34A' : '#DC2626', textTransform: 'capitalize' }}>{c.status}</span>
                  </div>
                </div>
              );
            })}
            {health && Object.values(health.checks).some(c => c.error) && (
              <div style={{ marginTop: '4px' }}>
                {Object.entries(health.checks).filter(([, c]) => c.error).map(([name, c]) => (
                  <div key={name} style={{ fontSize: '12px', color: '#DC2626' }}>{name}: {c.error}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
